/**
 * Authentication Service
 * 
 * Handles token acquisition for both browser and Teams contexts
 */

import { PublicClientApplication, AccountInfo, AuthenticationResult, SilentRequest, InteractionRequiredAuthError } from '@azure/msal-browser';
import * as microsoftTeams from '@microsoft/teams-js';
import { msalConfig, loginRequest, tokenRequest, isInTeams } from '@/config/authConfig';

class AuthService {
  private msalInstance: PublicClientApplication | null = null;
  private isTeamsContext: boolean = false;
  private teamsInitialized: boolean = false;
  
  // Token caching to avoid re-acquiring on every request
  private cachedToken: string | null = null;
  private tokenExpiryTime: number = 0;

  /**
   * Initialize MSAL and Teams SDK
   */
  async initialize(): Promise<void> {
    console.log('[AuthService] Initializing...');
    
    // Initialize MSAL
    this.msalInstance = new PublicClientApplication(msalConfig);
    await this.msalInstance.initialize();
    console.log('[AuthService] MSAL initialized');

    // Check if running in Teams
    this.isTeamsContext = isInTeams();
    
    if (this.isTeamsContext) {
      try {
        await microsoftTeams.app.initialize();
        this.teamsInitialized = true;
        console.log('[AuthService] Teams SDK initialized');
      } catch (error) {
        console.error('[AuthService] Failed to initialize Teams SDK:', error);
        this.isTeamsContext = false;
      }
    }

    // Handle redirect promise (for browser OAuth flow)
    try {
      const response = await this.msalInstance.handleRedirectPromise();
      if (response) {
        console.log('[AuthService] Handled redirect response:', response.account?.username);
      }
    } catch (error) {
      console.error('[AuthService] Error handling redirect:', error);
    }
  }

  /**
   * Get MSAL instance
   */
  getMsalInstance(): PublicClientApplication | null {
    return this.msalInstance;
  }

  /**
   * Check if running in Teams context
   */
  isTeams(): boolean {
    return this.isTeamsContext;
  }

  /**
   * Get current account
   */
  getAccount(): AccountInfo | null {
    if (!this.msalInstance) return null;
    
    const accounts = this.msalInstance.getAllAccounts();
    return accounts.length > 0 ? accounts[0] : null;
  }

  /**
   * Acquire token (unified method for both browser and Teams)
   * Uses cached token if still valid (5 min buffer before expiry)
   */
  async acquireToken(): Promise<string | null> {
    const isDebug = import.meta.env.DEV;
    
    // Check if cached token is still valid (with 5 min buffer)
    const now = Date.now();
    const bufferMs = 5 * 60 * 1000; // 5 minutes
    if (this.cachedToken && this.tokenExpiryTime > now + bufferMs) {
      if (isDebug) {
        console.log('[AuthService] ♻️ Using cached token (expires in', Math.floor((this.tokenExpiryTime - now) / 1000 / 60), 'min)');
      }
      return this.cachedToken;
    }
    
    if (isDebug) {
      console.log('='.repeat(80));
      console.log('[AuthService] 🔑 Acquiring new token...');
      console.log('[AuthService] Context:', this.isTeamsContext ? '📱 Teams' : '🌐 Browser');
      console.log('[AuthService] MSAL initialized:', !!this.msalInstance);
      console.log('[AuthService] Teams initialized:', this.teamsInitialized);
    }
    
    try {
      let token: string | null = null;
      
      if (this.isTeamsContext) {
        if (isDebug) console.log('[AuthService] → Using Teams SSO flow');
        token = await this.acquireTeamsToken();
      } else {
        if (isDebug) console.log('[AuthService] → Using Browser MSAL flow');
        token = await this.acquireBrowserToken();
      }
      
      if (token) {
        // Cache the token and set expiry (tokens typically valid for 1 hour)
        this.cachedToken = token;
        this.tokenExpiryTime = now + (60 * 60 * 1000); // 1 hour from now
        
        if (isDebug) {
          console.log('[AuthService] ✅ Token acquired and cached successfully');
          console.log('[AuthService] Token length:', token.length);
        }
      } else {
        if (isDebug) console.log('[AuthService] ❌ No token acquired');
      }
      
      if (isDebug) console.log('='.repeat(80));
      return token;
    } catch (error) {
      console.error('[AuthService] ❌ Error acquiring token:', error);
      if (isDebug) console.log('='.repeat(80));
      return null;
    }
  }

  /**
   * Acquire token for Teams SSO
   */
  private async acquireTeamsToken(): Promise<string | null> {
    console.log('[AuthService/Teams] 📱 Starting Teams SSO token acquisition');
    console.log('[AuthService/Teams] Teams initialized:', this.teamsInitialized);
    
    if (!this.teamsInitialized) {
      console.error('[AuthService/Teams] ❌ Teams SDK not initialized');
      return null;
    }

    try {
      console.log('[AuthService/Teams] Calling microsoftTeams.authentication.getAuthToken()...');
      
      // Get Teams SSO token using promise wrapper
      const token = await new Promise<string>((resolve, reject) => {
        microsoftTeams.authentication.getAuthToken({
          successCallback: (token: string) => {
            console.log('[AuthService/Teams] ✅ getAuthToken successCallback invoked');
            console.log('[AuthService/Teams] Token received, length:', token.length);
            resolve(token);
          },
          failureCallback: (error: string) => {
            console.error('[AuthService/Teams] ❌ getAuthToken failureCallback invoked');
            console.error('[AuthService/Teams] Error:', error);
            reject(new Error(error));
          },
        });
      });
      
      console.log('[AuthService/Teams] ✓ Teams SSO token acquired successfully');
      return token;
      
    } catch (error) {
      console.error('[AuthService/Teams] ❌ Teams token acquisition failed:', error);
      console.error('[AuthService/Teams] Error details:', JSON.stringify(error, null, 2));
      
      // Fallback to interactive auth
      console.log('[AuthService/Teams] 🔄 Attempting fallback to browser MSAL flow...');
      try {
        const fallbackToken = await this.acquireBrowserToken();
        if (fallbackToken) {
          console.log('[AuthService/Teams] ✅ Fallback auth succeeded');
        } else {
          console.error('[AuthService/Teams] ❌ Fallback auth returned null');
        }
        return fallbackToken;
      } catch (fallbackError) {
        console.error('[AuthService/Teams] ❌ Fallback auth also failed:', fallbackError);
        console.error('[AuthService/Teams] Fallback error details:', JSON.stringify(fallbackError, null, 2));
        return null;
      }
    }
  }

  /**
   * Acquire token for browser (MSAL)
   */
  private async acquireBrowserToken(): Promise<string | null> {
    console.log('[AuthService/Browser] 🌐 Starting browser MSAL token acquisition');
    console.log('[AuthService/Browser] MSAL instance present:', !!this.msalInstance);
    
    if (!this.msalInstance) {
      console.error('[AuthService/Browser] ❌ MSAL not initialized');
      return null;
    }

    const account = this.getAccount();
    console.log('[AuthService/Browser] Account lookup result:', account ? '✓ Found' : '✗ Not found');
    
    if (account) {
      console.log('[AuthService/Browser] Account details:', {
        username: account.username,
        name: account.name,
        homeAccountId: account.homeAccountId
      });
    }
    
    if (!account) {
      console.log('[AuthService/Browser] ⚠️ No account found, interactive login required');
      return null;
    }

    try {
      // Try silent token acquisition first
      console.log('[AuthService/Browser] 🔇 Attempting silent token acquisition...');
      const request: SilentRequest = {
        ...tokenRequest,
        account: account,
      };
      console.log('[AuthService/Browser] Request scopes:', request.scopes);

      const response = await this.msalInstance.acquireTokenSilent(request);
      console.log('[AuthService/Browser] ✅ Silent token acquired successfully');
      console.log('[AuthService/Browser] Token expiry:', new Date(response.expiresOn || '').toISOString());
      return response.accessToken;
      
    } catch (error) {
      console.error('[AuthService/Browser] ❌ Silent token acquisition failed');
      console.error('[AuthService/Browser] Error type:', error?.constructor?.name);
      console.error('[AuthService/Browser] Error:', error);
      
      if (error instanceof InteractionRequiredAuthError) {
        console.log('[AuthService/Browser] 🪟 Interaction required, falling back to popup...');
        
        try {
          // Fall back to interactive acquisition
          const response = await this.msalInstance.acquireTokenPopup(tokenRequest);
          console.log('[AuthService/Browser] ✅ Interactive token acquired successfully');
          console.log('[AuthService/Browser] Token expiry:', new Date(response.expiresOn || '').toISOString());
          return response.accessToken;
        } catch (popupError) {
          console.error('[AuthService/Browser] ❌ Popup token acquisition failed:', popupError);
          console.error('[AuthService/Browser] Popup error details:', JSON.stringify(popupError, null, 2));
          return null;
        }
      } else {
        console.error('[AuthService/Browser] ❌ Silent token acquisition failed (not interaction required)');
        console.error('[AuthService/Browser] Error details:', JSON.stringify(error, null, 2));
        return null;
      }
    }
  }

  /**
   * Login with redirect (browser only)
   */
  async loginWithRedirect(): Promise<void> {
    console.log('[AuthService] 🔐 Starting login redirect flow...');
    console.log('[AuthService] MSAL instance present:', !!this.msalInstance);
    
    if (!this.msalInstance) {
      console.error('[AuthService] ❌ MSAL not initialized, cannot login');
      throw new Error('MSAL not initialized');
    }

    console.log('[AuthService] Login request scopes:', loginRequest.scopes);
    console.log('[AuthService] Initiating redirect...');
    await this.msalInstance.loginRedirect(loginRequest);
    console.log('[AuthService] ✅ Redirect initiated (user will be redirected)');
  }

  /**
   * Login with popup (browser only)
   */
  async loginWithPopup(): Promise<AuthenticationResult | null> {
    console.log('[AuthService] 🔐 Starting login popup flow...');
    console.log('[AuthService] MSAL instance present:', !!this.msalInstance);

    try {
      console.log('[AuthService] Login request scopes:', loginRequest.scopes);
      console.log('[AuthService] Opening popup...');
      const response = await this.msalInstance!.loginPopup(loginRequest);
      console.log('[AuthService] ✅ Login successful');
      console.log('[AuthService] User:', response.account?.username);
      console.log('[AuthService] Account ID:', response.account?.homeAccountId);
      return response;
    } catch (error) {
      console.error('[AuthService] ❌ Popup login failed:', error);
      console.error('[AuthService] Error details:', JSON.stringify(error, null, 2));
      return null;
    }
  }

  /**
   * Logout
   */
  async logout(): Promise<void> {
    console.log('[AuthService] 🚪 Logout initiated');
    console.log('[AuthService] MSAL instance present:', !!this.msalInstance);
    
    if (!this.msalInstance) {
      console.log('[AuthService] ⚠️ MSAL not initialized, nothing to logout');
      return;
    }

    const account = this.getAccount();
    console.log('[AuthService] Account found:', !!account);
    
    if (account) {
      console.log('[AuthService] Logging out user:', account.username);
      await this.msalInstance.logoutRedirect({
        account: account,
      });
      console.log('[AuthService] ✅ Logout redirect initiated');
    } else {
      console.log('[AuthService] ⚠️ No account to logout');
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.getAccount() !== null;
  }
}

// Export singleton instance
export const authService = new AuthService();
